// src/pages/canteen/OrderListPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOrders,
  cancelOrder,
  updateOrderStatus,
  deleteOrder,
  submitFeedback,
  clearError,
} from "store/slices/orderSlice";
import { fetchFeedbacks } from "store/slices/feedbackSlice";
import {
  Loader2,
  MoreHorizontal,
  XCircle,
  CheckCircle,
  Clock,
  Package,
  Star,
  Trash2,
  ShoppingCart,
  IndianRupee,
  MoreVertical,
  Eye,
  FileText,
} from "lucide-react";
import DataTable from "components/common/DataTable";
import PageHeader from "components/common/PageHeader";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Textarea } from "components/ui/textarea";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { toast } from "sonner";
import { usePermissions } from "hooks/usePermissions";
import usePermissionSocket from "hooks/usePermissionSocket";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import useCanteenSocket from "hooks/useCanteenSocket";
import { canManageEverything, canManageOrder } from "utlis/authUtils";
import { restoreRole } from "store/slices/commonSlice";
import LogDateFilter from "components/common/LogDateFilter";
import { formatDateTime } from "utlis/formateDateTime";

const statusConfig = {
  pending: { label: "Pending", variant: "secondary", icon: Clock },
  preparing: { label: "Preparing", variant: "default", icon: Package },
  ready: { label: "Ready", variant: "outline", icon: CheckCircle },
  delivered: { label: "Delivered", variant: "success", icon: CheckCircle },
  // cancelled: { label: 'Cancelled', variant: 'destructive', icon: XCircle },
};

const OrderListPage = () => {
  useCanteenSocket();
  usePermissionSocket();

  const isSuperAdmin = canManageEverything();

  const canOrderManage = canManageOrder();

  const dispatch = useDispatch();
  const { orders, pagination, isLoading, error, stats } = useSelector(
    (s) => s.order
  );
  const { canRead, canUpdate, canDelete } = usePermissions("canteen_order_log");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const [statusOpen, setStatusOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  // Confirmation states
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [orderToAct, setOrderToAct] = useState(null);

  const [viewItemsOpen, setViewItemsOpen] = useState(false);
  const [selectedOrderItems, setSelectedOrderItems] = useState(null);

  const [dateFilters, setDateFilters] = useState({});

  // Optional: reset page when filters change
  // useEffect(() => {
  //   setPage(1);
  // }, [searchTerm, dateFilters]);

  const openItemsModal = (order) => {
    setSelectedOrderItems(order);
    setViewItemsOpen(true);
  };

  const [cancelReason, setCancelReason] = useState("");

  const { feedbacks = [] } = useSelector((state) => state.feedback);

  const submittedFeedbackOrderIds = useMemo(() => {
    return new Set(feedbacks.map((f) => f.order));
  }, [feedbacks]);

  const refetch = useCallback(() => {
    if (canRead) dispatch(fetchOrders({ page, pageSize, search: searchTerm }));
  }, [dispatch, page, pageSize, searchTerm, canRead]);

  // useEffect(() => { refetch(); }, [refetch]);

  // useEffect(() => {
  //   if (canRead) {
  //     dispatch(fetchOrders({ page, pageSize, search: searchTerm }));
  //     dispatch(fetchFeedbacks({ page: 1, pageSize: 10000 })); // Load all feedbacks
  //   }
  // }, [dispatch, canRead, page, pageSize, searchTerm]);

  useEffect(() => {
    if (!canRead) return;
    dispatch(
      fetchOrders({ page, pageSize, search: searchTerm, ...dateFilters })
    );
    dispatch(fetchFeedbacks({ page: 1, pageSize: 5000 })); // or keep as is
  }, [dispatch, canRead, page, pageSize, searchTerm, dateFilters]);

  //   useEffect(() => {
  //   if (canRead) {
  //     dispatch(fetchOrders({ page: 1, pageSize: 10 }));
  //   }
  // }, [dispatch, canRead]);

  // OrderListPage.jsx or CanteenDashboard.jsx
  // useEffect(() => {
  //   if (user?.role) {
  //     dispatch(fetchOrders({ page: 1, pageSize: 10 })); // Your normal API call
  //   }
  // }, [dispatch, user]);

  // useEffect(() => {
  //   if (canRead) {
  //       dispatch(fetchOrders({ page: 1, pageSize: 10000 }));
  //   }
  // }, [dispatch, canRead, page, pageSize, searchTerm]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const openStatusDialog = (order) => {
    if (!canUpdate) return toast.error("No permission");
    setSelectedOrder(order);
    setNewStatus(order.status);
    setStatusOpen(true);
  };

  const openFeedbackDialog = (order) => {
    if (order.status !== "delivered")
      return toast.error("Only delivered orders can have feedback");
    setSelectedOrder(order);
    setRating(5);
    setComment("");
    setFeedbackOpen(true);
  };

  // Cancel handlers with confirmation
  // const requestCancel = (order) => {
  //   if (order.status !== 'pending') return toast.error('Only pending orders can be cancelled');
  //   setOrderToAct(order);
  //   setCancelConfirmOpen(true);
  // };
  const requestCancel = (order) => {
    if (order.status !== "pending")
      return toast.error("Only pending orders can be cancelled");
    setOrderToAct(order);
    setCancelReason(""); // Reset reason
    setCancelConfirmOpen(true);
  };

  // const confirmCancel = async () => {
  //   await dispatch(cancelOrder(orderToAct.id)).unwrap();
  //   toast.success('Order cancelled successfully');
  //   setCancelConfirmOpen(false);
  //   setOrderToAct(null);
  // };

  const confirmCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error("Please provide a reason for cancellation");
      return;
    }

    try {
      // Pass reason to backend via updated thunk (assumes cancelOrder now accepts reason)
      await dispatch(
        cancelOrder({ id: orderToAct.id, reason: cancelReason.trim() })
      ).unwrap();
      toast.success("Order cancelled successfully");
      setCancelConfirmOpen(false);
      setOrderToAct(null);
      setCancelReason("");
    } catch (err) {
      toast.error(err || "Failed to cancel order");
    }
  };

  // Delete handlers with confirmation
  const requestDelete = (order) => {
    setOrderToAct(order);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    await dispatch(deleteOrder(orderToAct.id)).unwrap();
    toast.success("Order deleted successfully");
    setDeleteConfirmOpen(false);
    setOrderToAct(null);
  };

  const handleStatusUpdate = async () => {
    if (newStatus === selectedOrder.status) return setStatusOpen(false);
    await dispatch(
      updateOrderStatus({ id: selectedOrder.id, status: newStatus })
    ).unwrap();
    toast.success("Status updated");
    setStatusOpen(false);
  };

  const handleCancel = async (order) => {
    if (order.status !== "pending")
      return toast.error("Only pending orders can be cancelled");
    await dispatch(cancelOrder(order.id)).unwrap();
    toast.success("Order cancelled");
  };

  // const handleFeedback = async () => {
  //   await dispatch(submitFeedback({ id: selectedOrder.id, rating, comment })).unwrap();
  //   toast.success('Thank you for your feedback!');
  //   setFeedbackOpen(false);
  // };

  const handleFeedback = async () => {
    try {
      await dispatch(
        submitFeedback({
          id: selectedOrder.id,
          rating,
          comment: comment.trim() || null,
        })
      ).unwrap();

      toast.success("Thank you for your feedback!");
      setFeedbackOpen(false);
    } catch (err) {
      toast.error(err);
    }
  };

  const columns = [
    {
      accessorKey: "code",
      header: "Order Code",
      cell: ({ row }) => row.original.code ?? "-",
    },
    {
      id: "items_summary",
      header: "Items",
      size: 180,
      cell: ({ row }) => {
        const items = row.original.items_detail || [];
        if (items.length === 0)
          return <span className="text-muted-foreground">—</span>;

        const totalQty = items.reduce(
          (sum, i) => sum + (Number(i.quantity) || 1),
          0
        );

        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">
              {items.length} {items.length === 1 ? "item" : "items"} •{" "}
              {totalQty} pcs
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => openItemsModal(row.original)}
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
    {
      accessorKey: "order_time",
      header: "Order Time",
      cell: ({ row }) =>  (formatDateTime(row.original.order_time)),
    },
    {
      accessorKey: "employee_name",
      header: "Employee",
    },
    {
      accessorKey: "total_price",
      header: "Amount",
      cell: ({ row }) => `₹${row.original.total_price}`,
    },
    // {
    //   accessorKey: 'status',
    //   header: 'Status',
    //   cell: ({ row }) => {
    //     const status = row.original.status;
    //     const config = statusConfig[status] || statusConfig.pending;
    //     const Icon = config.icon;
    //     return (
    //       <Badge variant={config.variant}>
    //         <Icon className="w-3 h-3 mr-1" />
    //         {status}
    //       </Badge>
    //     );
    //   },
    // },

    {
      accessorKey: "status_display",
      header: "Status",
      cell: ({ row }) => {
        const status = row.original.status_display;

        let variant = "secondary";
        let Icon = Clock;

        // inline conditions (no external config)
        if (status === "pending") {
          variant = "secondary";
          Icon = Clock;
        } else if (status === "preparing") {
          variant = "default";
          Icon = Package;
        } else if (status === "ready") {
          variant = "outline";
          Icon = CheckCircle;
        } else if (status === "delivered") {
          variant = "success";
          Icon = CheckCircle;
        } else if (status === "cancelled") {
          variant = "destructive";
          Icon = XCircle;
        }

        return (
          <Badge variant={variant}>
            <Icon className="w-3 h-3 mr-1" />
            {status}
          </Badge>
        );
      },
    },

    {
      id: "actions",
      header: "Actions",
      size: 80,
      cell: ({ row }) => {
        const order = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Open actions menu</span>
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-48">
              {/* Update Status */}
              {canUpdate &&
                (isSuperAdmin || canOrderManage) &&
                order.status !== "cancelled" &&
                order.status !== "delivered" && (
                  <DropdownMenuItem onSelect={() => openStatusDialog(order)}>
                    <Package className="mr-2 h-4 w-4" />
                    Update Status
                  </DropdownMenuItem>
                )}

              {/* Cancel Order */}
              {(order.status === "pending" || isSuperAdmin) && (
                <DropdownMenuItem
                  onSelect={() => requestCancel(order)}
                  className="text-destructive focus:text-destructive"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancel Order
                </DropdownMenuItem>
              )}

              {/* Rate / Feedback */}
              {/* {order.status === 'delivered' && !order.feedback && (
                <DropdownMenuItem onSelect={() => openFeedbackDialog(order)}>
                  <Star className="mr-2 h-4 w-4" />
                  Rate Order
                </DropdownMenuItem>
              )} */}
              {order.status === "delivered" &&
                !submittedFeedbackOrderIds.has(order.id) && (
                  <DropdownMenuItem onSelect={() => openFeedbackDialog(order)}>
                    <Star className="mr-2 h-4 w-4" />
                    Rate Order
                  </DropdownMenuItem>
                )}

              {/* Delete Order */}
              {(canDelete || isSuperAdmin) && (
                <DropdownMenuItem
                  onSelect={() => requestDelete(order)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Order
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  if (!canRead)
    return (
      <div className="p-8 text-center text-muted-foreground">No access</div>
    );

  return (
    <div classCauses="space-y-6">
      <PageHeader
        title="Order List"
        description="View and rate canteen orders"
      />
      {/* <LogDateFilter onFilter={setDateFilters} /> */}
      <div className="mb-5">
        <LogDateFilter onFilter={(f) => setDateFilters(f)} />
      </div>
      <DataTable
        data={orders}
        totalCount={pagination.count}
        columns={columns}
        searchPlaceholder="Search orders..."
        pageIndex={page - 1}
        pageSize={pageSize}
        pageCount={Math.ceil(pagination.count / pagination.page_size)}
        onPageChange={(i) => setPage(i + 1)}
        onPageSizeChange={setPageSize}
        onSearch={setSearchTerm}
        isLoading={isLoading}
        showExport={false}
        showImport={false}
        onDelete={async (id) => {
          await dispatch(deleteOrder(id));
          refetch();
        }}
        onRestore={async (id) => {
          await dispatch(restoreRole({ entityType: "orders", id }));
          refetch();
        }}
      />

      {/* STATUS UPDATE DIALOG */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Order Status</DialogTitle>
            <DialogDescription>
              Order by {selectedOrder?.employee_name}
            </DialogDescription>
          </DialogHeader>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(statusConfig).map(([key, { label }]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setStatusOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleStatusUpdate}>Update</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* FEEDBACK DIALOG */}
      <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rate Your Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Rating</label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((r) => (
                  <button key={r} onClick={() => setRating(r)}>
                    <Star
                      className={`w-8 h-8 ${
                        r <= rating
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-muted"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <Textarea
              placeholder="Optional comment..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setFeedbackOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleFeedback}>Submit Feedback</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CANCEL CONFIRMATION MODAL */}
      {/* <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this order?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the order by <strong>{orderToAct?.employee_name}</strong>
              (₹{orderToAct?.total_price})? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Order</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} className="bg-destructive text-destructive-foreground">
              Yes, Cancel Order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog> */}

      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Order?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the order by{" "}
              <strong>{orderToAct?.employee_name}</strong> (₹
              {orderToAct?.total_price})?
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="cancel-reason">
                Reason for cancellation{" "}
                <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="cancel-reason"
                placeholder="e.g., Changed mind, wrong items selected..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="mt-2"
                rows={3}
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCancelReason("")}>
              Keep Order
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancel}
              className="bg-destructive text-destructive-foreground"
              disabled={!cancelReason.trim()}
            >
              Yes, Cancel Order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* DELETE CONFIRMATION MODAL */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Want to delete order?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the order by{" "}
              <strong>{orderToAct?.employee_name}</strong>
              from the system. This action <strong>cannot be undone</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Order</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground"
            >
              Yes, Delete Forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={viewItemsOpen} onOpenChange={setViewItemsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between mt-5">
              <span>Order Items – {selectedOrderItems?.code}</span>
              <Badge
                variant={
                  selectedOrderItems?.status === "delivered"
                    ? "success"
                    : selectedOrderItems?.status === "cancelled"
                    ? "destructive"
                    : "secondary"
                }
              >
                {selectedOrderItems?.status_display ||
                  selectedOrderItems?.status}
              </Badge>
            </DialogTitle>
            <DialogDescription className="flex flex-col gap-1">
              <span>
                Employee: <strong>{selectedOrderItems?.employee_name}</strong>
              </span>
              <span>
                Ordered:{" "}
                {new Date(selectedOrderItems?.order_time).toLocaleString()}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">Item</th>
                  <th className="text-center p-3 font-medium">Qty</th>
                  <th className="text-right p-3 font-medium">Unit Price</th>
                  <th className="text-right p-3 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {selectedOrderItems?.items_detail?.map((item, index) => (
                  <tr
                    key={index}
                    className="border-b last:border-0 hover:bg-muted/40 transition-colors"
                  >
                    <td className="p-3 font-medium">{item.menu_name}</td>
                    <td className="p-3 text-center">{item.quantity || 1}</td>
                    <td className="p-3 text-right">
                      ₹{Number(item.unit_price || item.menu_price).toFixed(2)}
                    </td>
                    <td className="p-3 text-right font-medium">
                      ₹
                      {(
                        Number(item.quantity || 1) *
                        Number(item.unit_price || item.menu_price)
                      ).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t font-medium">
                <tr>
                  <td colSpan={3} className="p-3 text-right">
                    Total Amount
                  </td>
                  <td className="p-3 text-right">
                    ₹{Number(selectedOrderItems?.total_price || 0).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setViewItemsOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderListPage;
